import { useState, useEffect, useCallback } from "react";
import { ConfigProvider, Layout } from "antd";
import liff from "@line/liff";
import { jwtDecode } from "jwt-decode";
import { setUnauthorizedHandler } from "api/axios";
import api from "api";
import { MainStore } from "core/store";
import PrivateRoute from "routes/private-route";
import PublicRoute from "routes/public-route";
import Loading from "components/loading";

const { Content } = Layout;

function App() {
  const [userData, setUserData] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [liffInitialized, setLiffInitialized] = useState(false);
  const [isNotFoundUser, setIsNotFoundUser] = useState(false);

  const loginGofalo = useCallback(async (idToken) => {
    const isLoggin = localStorage.getItem("isLogin") === "true";
    try {
      if (!isLoggin) {
        await api.authApi.login({ idToken });
        localStorage.setItem("isLogin", true);
      }
    } catch (error) {
      console.error("Backend login error:", error);
      setIsNotFoundUser(true);
    } finally {
      setIsReady(true);
    }
  }, []);

  const initLine = useCallback(async () => {
    if (liffInitialized) return;

    try {
      await liff.init({
        liffId: process.env.REACT_APP_LINE_LIFF_ID,
        withLoginOnExternalBrowser: true,
      });

      setLiffInitialized(true);

      if (!liff.isLoggedIn()) {
        liff.login();
        return;
      }

      const idToken = liff.getIDToken();
      if (!idToken) {
        console.warn("No ID token, force LIFF login");
        liff.login();
        return;
      }

      const decoded = jwtDecode(idToken);
      const now = Math.floor(Date.now() / 1000);

      if (decoded.exp < now) {
        console.warn("ID token expired, login backend only");
        await loginGofalo(idToken);
        return;
      }

      await loginGofalo(idToken);
    } catch (err) {
      console.error("LIFF init failed:", err);
      setIsReady(true);
    }
  }, [liffInitialized, loginGofalo]);

  useEffect(() => {
    const run = async () => {
      setUnauthorizedHandler(async () => {
        console.log("Unauthorized → login LIFF ใหม่");
        localStorage.clear();
        localStorage.setItem("isLogin", false);
        await initLine();
      });
      await initLine();
    };
    run();
  }, []);

  // Skip loading gate for public routes (insurance, payment, etc.)
  const publicPaths = ["/insurance", "/payment", "/payment_success", "/login"];
  const isPublicRoute = publicPaths.some((p) => window.location.pathname.startsWith(p));
  if (!isReady && !isPublicRoute) return <Loading />;

  const theme = {
    token: {
      borderRadius: 15,
      colorPrimary: "#07C756",
      colorText: "black",
    },
    components: {
      Rate: { starSize: 24 },
      Switch: { trackHeightSM: 30, trackHeight: 30, handleSize: 30 },
    },
  };

  return (
    <ConfigProvider theme={theme}>
      <MainStore.Provider
        value={{
          userData,
          setUserData,
          isReady,
          setReloadData: () => {},
        }}
      >
        <Layout>
          <Content>
            <PrivateRoute isNotFoundUser={isNotFoundUser} />
            <PublicRoute />
          </Content>
        </Layout>
      </MainStore.Provider>
    </ConfigProvider>
  );
}

export default App;
